import { useState } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { useGetTopics } from "../../../../api/queries";
import Spinner from "../../../../componentLibrary/Spinner";
import type { ModuleI } from "../../../../types";

interface TopicPickerProps {
  currentTopicId: string;
  selectedTopic: ModuleI;
  onSelect: (slug: string) => void;
}

export default function TopicPicker({
  currentTopicId,
  selectedTopic,
  onSelect,
}: TopicPickerProps) {
  const [open, setOpen] = useState(false);

  const { data: topics, isLoading } = useGetTopics(undefined, undefined, {
    enabled: open,
  });

  const handleSelect = (slug: string) => {
    setOpen(false);
    onSelect(slug);
  };

  return (
    <div className="relative w-fit">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="group flex items-center gap-2 text-left"
      >
        <img
          src={selectedTopic.icon.src}
          alt={selectedTopic.icon.alt || selectedTopic.title}
          className="w-8 h-8 object-contain shrink-0"
        />
        <span className="text-2xl font-bold text-gray-800 group-hover:text-gray-600 transition-colors">
          {selectedTopic.title} Toolbox
        </span>
        <ChevronDownIcon
          className={`w-5 h-5 text-gray-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 z-20 w-64 rounded-lg border border-gray-200 bg-white shadow-lg py-1 max-h-72 overflow-y-auto">
          {isLoading || !topics ? (
            <div className="flex justify-center py-4">
              <Spinner className="w-5" />
            </div>
          ) : (
            topics.map((t) => (
              <button
                key={t._id}
                type="button"
                onClick={() => handleSelect(t.slug ?? "")}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm text-left hover:bg-gray-50 transition-colors ${
                  t.slug === currentTopicId || t._id === currentTopicId
                    ? "font-semibold text-gray-900 bg-gray-50"
                    : "text-gray-700"
                }`}
              >
                <img
                  src={t.icon.src}
                  alt={t.icon.alt || t.title}
                  className="w-5 h-5 object-contain shrink-0"
                />
                {t.title}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
